import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../services/api';

// Estado inicial
const initialState = {
  colaborador: null,
  token: localStorage.getItem('colaboradorToken'),
  loading: true,
  isAuthenticated: false
};

// Tipos de ações
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_COLABORADOR: 'SET_COLABORADOR',
  SET_TOKEN: 'SET_TOKEN',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  UPDATE_COLABORADOR: 'UPDATE_COLABORADOR',
  CLEAR_AUTH: 'CLEAR_AUTH'
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case AUTH_ACTIONS.SET_COLABORADOR:
      return { 
        ...state, 
        colaborador: action.payload,
        isAuthenticated: !!action.payload
      };
    
    case AUTH_ACTIONS.SET_TOKEN:
      return { ...state, token: action.payload };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        colaborador: action.payload.colaborador,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        colaborador: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    
    case AUTH_ACTIONS.UPDATE_COLABORADOR:
      return {
        ...state,
        colaborador: { ...state.colaborador, ...action.payload }
      };
    
    case AUTH_ACTIONS.CLEAR_AUTH:
      return {
        ...state,
        colaborador: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    
    default:
      return state;
  }
}

// Context
const AuthColaboradorContext = createContext();

// Provider
export const AuthColaboradorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Verificar token na inicialização
  useEffect(() => {
    const verificarToken = async () => {
      const token = localStorage.getItem('colaboradorToken');
      
      if (token) {
        try {
          // Configurar token no header
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Verificar se o token é válido
          const response = await api.get('/auth-colaborador/me');
          
          if (response.data.success) {
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                colaborador: response.data.data,
                token
              }
            });
          } else {
            // Token inválido
            localStorage.removeItem('colaboradorToken');
            delete api.defaults.headers.common['Authorization'];
            dispatch({ type: AUTH_ACTIONS.CLEAR_AUTH });
          }
        } catch (error) {
          console.error('Erro ao verificar token:', error);
          localStorage.removeItem('colaboradorToken');
          delete api.defaults.headers.common['Authorization'];
          dispatch({ type: AUTH_ACTIONS.CLEAR_AUTH });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    verificarToken();
  }, []);

  // Função de login
  const login = async (email, senha) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.post('/auth-colaborador/login', {
        email,
        senha
      });

      if (response.data.success) {
        const { colaborador, token } = response.data.data;
        
        // Salvar token no localStorage
        localStorage.setItem('colaboradorToken', token);
        
        // Configurar token no header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { colaborador, token }
        });

        toast.success('Login realizado com sucesso!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Erro no login');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      
      const message = error.response?.data?.message || 'Erro ao fazer login';
      toast.error(message);
      
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: false, message };
    }
  };

  // Função de logout
  const logout = async () => {
    try {
      // Chamar endpoint de logout (opcional)
      await api.post('/auth-colaborador/logout');
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('colaboradorToken');
      delete api.defaults.headers.common['Authorization'];
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logout realizado com sucesso!');
      
      // Redirecionar para login
      navigate('/login-colaborador');
    }
  };

  // Função para atualizar dados do colaborador
  const updateColaborador = async (dados) => {
    try {
      const response = await api.put('/auth-colaborador/me', dados);
      
      if (response.data.success) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_COLABORADOR,
          payload: response.data.data
        });
        toast.success('Dados atualizados com sucesso!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Erro ao atualizar dados');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Erro ao atualizar colaborador:', error);
      const message = error.response?.data?.message || 'Erro ao atualizar dados';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Função para alterar senha
  const changePassword = async (senhaAtual, novaSenha) => {
    try {
      const response = await api.put('/auth-colaborador/change-password', {
        senhaAtual,
        novaSenha
      });
      
      if (response.data.success) {
        toast.success('Senha alterada com sucesso!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Erro ao alterar senha');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      const message = error.response?.data?.message || 'Erro ao alterar senha';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Função para renovar token
  const refreshToken = async () => {
    try {
      const token = localStorage.getItem('colaboradorToken');
      if (!token) return { success: false };

      const response = await api.post('/auth-colaborador/refresh', { token });
      
      if (response.data.success) {
        const { token: newToken } = response.data;
        localStorage.setItem('colaboradorToken', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: newToken });
        return { success: true };
      } else {
        return { success: false };
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      return { success: false };
    }
  };

  // Função para verificar permissões
  const hasPermission = (modulo, acao) => {
    if (!state.colaborador) return false;
    
    // Administradores têm acesso total
    if (state.colaborador.perfil === 'administrador') return true;
    
    // Verificar permissões específicas
    if (state.colaborador.permissoes && state.colaborador.permissoes.length > 0) {
      const permissao = state.colaborador.permissoes.find(p => p.modulo === modulo);
      if (permissao && permissao.acoes.includes(acao)) {
        return true;
      }
    }
    
    // Verificar permissões baseadas no perfil
    const permissoesPorPerfil = {
      supervisor: {
        ponto: ['visualizar', 'aprovar', 'editar'],
        frota: ['visualizar', 'criar', 'editar'],
        rh: ['visualizar', 'editar'],
        relatorios: ['visualizar']
      },
      financeiro: {
        ponto: ['visualizar'],
        frota: ['visualizar'],
        rh: ['visualizar'],
        financeiro: ['visualizar', 'criar', 'editar'],
        relatorios: ['visualizar']
      },
      motorista: {
        ponto: ['visualizar', 'criar'],
        frota: ['visualizar']
      },
      monitor: {
        ponto: ['visualizar', 'criar'],
        frota: ['visualizar'],
        chamada: ['visualizar', 'criar', 'editar']
      },
      limpeza: {
        ponto: ['visualizar', 'criar'],
        frota: ['visualizar']
      },
      administrativo: {
        ponto: ['visualizar', 'aprovar'],
        frota: ['visualizar'],
        rh: ['visualizar', 'editar'],
        relatorios: ['visualizar']
      },
      coordenador: {
        ponto: ['visualizar', 'aprovar', 'editar'],
        frota: ['visualizar', 'criar', 'editar'],
        rh: ['visualizar', 'editar'],
        relatorios: ['visualizar']
      }
    };
    
    const permissoesPerfil = permissoesPorPerfil[state.colaborador.perfil];
    
    return permissoesPerfil && 
           permissoesPerfil[modulo] && 
           permissoesPerfil[modulo].includes(acao);
  };

  // Função para verificar se é um perfil específico
  const isProfile = (perfil) => {
    return state.colaborador?.perfil === perfil;
  };

  // Função para verificar se é motorista
  const isMotorista = () => isProfile('motorista');
  
  // Função para verificar se é supervisor
  const isSupervisor = () => isProfile('supervisor');
  
  // Função para verificar se é financeiro
  const isFinanceiro = () => isProfile('financeiro');
  
  // Função para verificar se é administrador
  const isAdmin = () => isProfile('administrador');

  // Valor do contexto
  const value = {
    colaborador: state.colaborador,
    token: state.token,
    loading: state.loading,
    isAuthenticated: state.isAuthenticated,
    login,
    logout,
    updateColaborador,
    changePassword,
    refreshToken,
    hasPermission,
    isProfile,
    isMotorista,
    isSupervisor,
    isFinanceiro,
    isAdmin
  };

  return (
    <AuthColaboradorContext.Provider value={value}>
      {children}
    </AuthColaboradorContext.Provider>
  );
};

// Hook para usar o contexto
export const useAuthColaborador = () => {
  const context = useContext(AuthColaboradorContext);
  if (!context) {
    throw new Error('useAuthColaborador deve ser usado dentro de um AuthColaboradorProvider');
  }
  return context;
};

export default AuthColaboradorContext;

